import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"

import { LinkToHomepage } from "@/components/error/LinkToHomepage"
import { Button } from "@/components/ui/button"
import type { ErrorType } from "@/lib/clients"
import { isRateLimitedError } from "@/lib/utils"
import BoundlessLogo from "@/svg/boundless/logo.svg?react"
import ErrorIllustration from "@/svg/illustrations/error.svg?react"

type GenericError = Error | ErrorType<unknown>

/**
 * @summary Displays a generic error page to customer.
 *
 * When in Development mode, the error message is displayed.
 *
 * Rate-limited errors (429) get a "please wait" page instead, which makes no
 * API calls, since they would be rate limited too and leave the page blank.
 */
export function GenericErrorComponent({ error }: { error: GenericError }) {
  const isRateLimited = isRateLimitedError(error)

  return (
    <div className="flex h-full flex-col items-center justify-around space-y-8 p-4">
      <BoundlessLogo className="h-12 w-auto" />
      <ErrorIllustration className="w-full max-w-md" />
      <div className="text-center">
        <h1 className="text-4xl font-extrabold">
          {isRateLimited ? "Too many requests." : "Houston, we have a problem."}
        </h1>

        {isRateLimited ? (
          <p className="mx-auto mt-4 max-w-md text-balance text-neutral-500">
            Please wait a moment before trying again.
          </p>
        ) : import.meta.env.DEV ? (
          <div className="mt-6">
            <pre
              className="rounded-md border border-red-500 p-2 text-sm text-red-500"
              style={{
                fontSize: ".7em",
                overflow: "auto",
              }}
            >
              {error.message ? <code>{error.message}</code> : null}
            </pre>
          </div>
        ) : (
          <SupportMessage />
        )}

        {isRateLimited ? (
          <Button className="mt-6" onClick={() => window.location.reload()}>
            Reload
          </Button>
        ) : (
          <LinkToHomepage>
            <Button className="mt-6">Back to Homepage</Button>
          </LinkToHomepage>
        )}
      </div>
    </div>
  )
}

/**
 * A separate component so the config query only runs when it's rendered, even
 * when callers invoke `GenericErrorComponent` as a plain function.
 */
function SupportMessage() {
  const { data: boundlessConfig } = usePublicBoundlessConfigRetrieveSuspense()

  return (
    <p className="mx-auto mt-4 max-w-md text-balance text-neutral-500">
      Something went wrong on our end. <br />
      We&apos;re working on it - please refresh or try again shortly. <br />
      If the issue continues, contact{" "}
      <a
        href={`mailto:${boundlessConfig.supportEmail}`}
        className="hover:underline"
      >
        {boundlessConfig.supportEmail}
      </a>
      .
    </p>
  )
}
